import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Badge, Card, type BadgeTone } from '@safetyhub/ui';
import { useParams } from 'react-router-dom';
import type { ExternalSignatureReportView } from '@safetyhub/shared';
import { ExternalReportContent } from './ExternalReportContent';

export type ExternalPageState =
  | 'loading'
  | 'ready'
  | 'processing'
  | 'success'
  | 'error';

export interface ExternalApiError {
  code: string;
  message: string;
  /** delete-approval의 `ALREADY_PROCESSED.status` 같은 부가 정보 */
  status?: string;
}

export type LoaderResult<TInfo> =
  | { ok: true; info: TInfo }
  | { ok: false; error: ExternalApiError };

export interface ExternalSignatureShellProps<TInfo, TResult = void> {
  /** 토큰 조회 로더. 성공 시 info 반환, 실패 시 error 객체 반환 */
  loader: (token: string) => Promise<LoaderResult<TInfo>>;
  /** info에서 렌더용 report를 추출 */
  extractReport: (info: TInfo) => ExternalSignatureReportView;
  /** 툴바 설정 */
  toolbar: {
    badgeLabel: (info: TInfo) => string;
    badgeTone: BadgeTone;
    title: (info: TInfo) => string;
    subtitle: (info: TInfo) => string;
    actionLabel: string;
    actionButtonClassName: string;
    /**
     * 제목·부제 아래 한 줄로 붙는 **문서 고지**(선택). 문서를 넘기기 전에 알아야 할 사실을
     * 페이지가 넣는다 — 셸은 도메인 어휘를 모르고 **줄이 왔는지만** 본다.
     *
     * 선택 인자인 이유는 이 셸을 쓰는 세 페이지의 독자가 다르기 때문이다: 결재자는 *승인할지*를
     * 정하러 오고, 현장담당자(`contact-signature`)·삭제 승인자는 다른 것을 하러 온다. 넘기지
     * 않으면 그 페이지들은 한 글자도 안 바뀐다.
     */
    notice?: (info: TInfo) => string | null | undefined;
  };
  /** 모달 렌더러. onSubmit이 action을 실행하고 결과를 반환 */
  renderModal: (args: {
    info: TInfo;
    token: string;
    isOpen: boolean;
    onClose: () => void;
    onSubmitted: (result: TResult) => void;
    /** processing 화면으로 전환 */
    setProcessing: () => void;
    /** 에러 화면으로 전환 */
    setError: (error: ExternalApiError) => void;
  }) => ReactNode;
  /** 로딩 화면 메시지 */
  loadingMessage?: string;
  /** 처리 중 화면 메시지 */
  processingMessage?: string;
  /** 성공 화면 렌더러 */
  renderSuccess: (result: TResult | null) => ReactNode;
  /** 에러 코드 → 제목 매핑 */
  errorTitleMap?: Record<string, string>;
  /** loader가 특정 code로 실패했을 때 커스텀 렌더 (예: delete-approval의 ALREADY_PROCESSED) */
  renderCustomError?: (error: ExternalApiError) => ReactNode | null;
  /** 스피너/배경 색상 (brand) */
  spinnerColorClass?: string;
}

/**
 * 외부 서명 페이지 공통 shell.
 *
 * `/approval/:token`, `/contact-signature/:token`, `/delete-approval/:token`
 * 3개 페이지가 공유하는 상태머신 + 문서 프레임 + 모달 프레임.
 *
 * - 로딩/에러/처리/성공 상태 화면 공통화
 * - 보고서 렌더는 `ExternalReportContent`(Composer 렌더 코어) 하나
 * - 가변 요소는 props로 주입 (loader/toolbar/modal/successView)
 */
export function ExternalSignatureShell<TInfo, TResult = void>({
  loader,
  extractReport,
  toolbar,
  renderModal,
  loadingMessage = '정보를 불러오는 중...',
  processingMessage = '처리하고 있습니다...',
  renderSuccess,
  errorTitleMap,
  renderCustomError,
  spinnerColorClass = 'border-brand-600',
}: ExternalSignatureShellProps<TInfo, TResult>) {
  const { token } = useParams<{ token: string }>();
  const [pageState, setPageState] = useState<ExternalPageState>('loading');
  const [info, setInfo] = useState<TInfo | null>(null);
  const [error, setError] = useState<ExternalApiError | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [successResult, setSuccessResult] = useState<TResult | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    (async () => {
      try {
        const result = await loader(token);
        if (cancelled) return;
        if (result.ok) {
          setInfo(result.info);
          setPageState('ready');
        } else {
          setError(result.error);
          setPageState('error');
        }
      } catch {
        if (cancelled) return;
        setError({
          code: 'NETWORK_ERROR',
          message: '서버에 연결할 수 없습니다. 잠시 후 다시 시도해주세요.',
        });
        setPageState('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, loader]);

  const handleSubmitted = useCallback((result: TResult) => {
    setShowModal(false);
    setSuccessResult(result);
    setPageState('success');
  }, []);

  const handleSetProcessing = useCallback(() => {
    setShowModal(false);
    setPageState('processing');
  }, []);

  const handleSetError = useCallback((err: ExternalApiError) => {
    setShowModal(false);
    setError(err);
    setPageState('error');
  }, []);

  const report = info ? extractReport(info) : null;

  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-ink-50 flex items-center justify-center">
        <div className="text-center">
          <div
            className={`animate-spin rounded-full h-12 w-12 border-b-2 ${spinnerColorClass} mx-auto`}
          />
          <p className="mt-4 text-ink-600">{loadingMessage}</p>
        </div>
      </div>
    );
  }

  if (pageState === 'processing') {
    return (
      <div className="min-h-screen bg-ink-50 flex items-center justify-center">
        <div className="text-center">
          <div
            className={`animate-spin rounded-full h-12 w-12 border-b-2 ${spinnerColorClass} mx-auto`}
          />
          <p className="mt-4 text-ink-600">{processingMessage}</p>
          <p className="mt-2 text-sm text-ink-500">잠시만 기다려주세요</p>
        </div>
      </div>
    );
  }

  if (pageState === 'error' && error) {
    // renderCustomError로 우선 커스텀 처리 시도
    if (renderCustomError) {
      const custom = renderCustomError(error);
      if (custom) return <>{custom}</>;
    }
    return (
      <div className="min-h-screen bg-ink-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-card shadow-lg p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
            <span className="h-8 w-8 text-red-600 text-2xl">&#9888;</span>
          </div>
          <h1 className="text-xl font-semibold text-ink-900 mb-2">
            {errorTitleMap?.[error.code] || '오류가 발생했습니다'}
          </h1>
          <p className="text-ink-600 mb-6">{error.message}</p>
          <div className="text-sm text-ink-500">
            <p>문제가 지속되면 담당자에게 문의해주세요.</p>
          </div>
        </div>
      </div>
    );
  }

  if (pageState === 'success') {
    return <>{renderSuccess(successResult)}</>;
  }

  if (!info || !report) return null;

  // 툴바 내용(배지·제목·부제·액션 버튼).
  const noticeLine = toolbar.notice?.(info);
  const toolbarInner = (
    <>
      <div className="flex items-center justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Badge tone={toolbar.badgeTone} shape="rounded">
              {toolbar.badgeLabel(info)}
            </Badge>
            <h1 className="text-sm font-medium text-ink-900 truncate">
              {toolbar.title(info)}
            </h1>
          </div>
          <p className="text-xs text-ink-500 mt-0.5 truncate">
            {toolbar.subtitle(info)}
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className={`ml-3 shrink-0 px-4 py-2 text-white rounded-control font-medium text-sm ${toolbar.actionButtonClassName}`}
        >
          {toolbar.actionLabel}
        </button>
      </div>
      {/* 부제와 달리 `truncate`하지 않는다 — 고지는 끝까지 읽혀야 뜻이 서는 문장이다. */}
      {noticeLine && (
        <p className="mt-2 text-xs text-amber-700" data-testid="external-report-notice">
          {noticeLine}
        </p>
      )}
    </>
  );

  const modalNode = renderModal({
    info,
    token: token || '',
    isOpen: showModal,
    onClose: () => setShowModal(false),
    onSubmitted: handleSubmitted,
    setProcessing: handleSetProcessing,
    setError: handleSetError,
  });

  // 렌더 코어(PagedComposerView)가 **자체 페이지네이션**을 가진다 — DocumentViewer의 페이지 분할
  // (usePrintPages)로 감싸면 이중 분할되어 페이지 넘김이 깨진다. 상세(ComposerReportDetail)·공유
  // (ComposedSharedView) 모두 DocumentViewer 없이 직접 렌더하므로 이 surface도 동형 프레임이다.
  return (
    <>
      <div className="min-h-screen bg-ink-50 py-4 sm:py-8 px-4">
        <div className="max-w-4xl xl:max-w-6xl mx-auto">
          <Card padding="none" radius="lg" elevation="md" className="mb-4 px-4 py-3">
            {toolbarInner}
          </Card>
          {/* 여백이 화면폭별로 갈려(p-2 sm:p-4) padding prop을 끄고 className으로 준다 */}
          <Card padding="none" radius="lg" elevation="md" className="p-2 sm:p-4">
            <ExternalReportContent report={report} />
          </Card>
        </div>
      </div>
      {modalNode}
    </>
  );
}
